"use client";

import type {
  ProjectDataSourceDescriptor,
  ProjectDataTraceIndex,
} from "@/types/aicheck";

const DB_NAME = "aicheck-project-data-details";
const DB_VERSION = 1;
const STORE_NAME = "details";

export interface ProjectDataSourceDetailEntry {
  detailsKey: string;
  sourceKey: string;
  conversionId?: string;
  source: ProjectDataSourceDescriptor;
  conversionReport?: Record<string, unknown>;
  provenance?: ProjectDataTraceIndex;
  warningDetails?: string[];
  cachedAt: string;
}

export function buildProjectDataSourceDetailsKey(sourceKey: string): string {
  return `project-data-source:${sourceKey}`;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "detailsKey" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB open failed"));
  });
}

export async function readProjectDataSourceDetails(
  detailsKey: string | null | undefined,
): Promise<ProjectDataSourceDetailEntry | null> {
  if (!detailsKey) return null;
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get(detailsKey);
    request.onsuccess = () =>
      resolve(
        (request.result as ProjectDataSourceDetailEntry | undefined) ?? null,
      );
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB read failed"));
    tx.oncomplete = () => db.close();
    tx.onerror = () => db.close();
  });
}

export async function writeProjectDataSourceDetails(
  entry: ProjectDataSourceDetailEntry,
): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(entry);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error ?? new Error("IndexedDB write failed"));
    };
  });
}
